import { Box } from '@mui/material';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import type {
  CohortHistoryChartConfig,
  DataPoint,
} from '@/features/chartsView';
import {
  LineChartWrapper,
  TABLEAU_20_COLORS,
  useHoveredSplitOption,
  useLineChartParams,
} from '@/features/chartsView';
import { useDataFetcher } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import { isDateDimension, isMediaDimension } from '@/shared/types';
import { Loader } from '@/shared/ui';
import { CHART_BASE_DIMENSION } from '../../model/constants';
import { formatSnapshotDttm } from '../../model/formatSnapshotDttm';
import {
  hasVisibleCohortData,
  narrowToVisibleSnapshots,
  resolveSelectedCohortCutoffs,
  trimImmatureCohorts,
} from '../../model/resolveCohortCutoff';
import { isChronologicalSplit, selectMeta } from '../../model/slice';
import type { CohortHistoryChartDataResponse } from '../../model/types';

const SeriesChart = lazy(() =>
  import('@/features/chartsView').then(({ SeriesChart }) => {
    return {
      default: SeriesChart<typeof CHART_BASE_DIMENSION>,
    };
  }),
);

const IMMATURE_COHORT_MESSAGE =
  'Every selected cohort is still filling up, so there are no complete snapshots yet. Check an earlier cohort to see its data.';

interface Props {
  config: CohortHistoryChartConfig;
  onGetData: () => Promise<CohortHistoryChartDataResponse>;
  onDataChange?: (data?: CohortHistoryChartDataResponse) => void;
}

export function Chart({ config, onGetData, onDataChange }: Props) {
  const historyMeta = useAppSelector(selectMeta);
  const isMediaContent = historyMeta
    ? isMediaDimension(historyMeta.config, config.splitDimension)
    : false;
  const isDateContent = historyMeta
    ? isDateDimension(historyMeta.config, config.splitDimension)
    : false;
  const hoveredOption = useHoveredSplitOption();
  const { data, dataStatus } = useDataFetcher(onGetData, {
    keepPreviousData: true,
  });
  const splitOptions = useMemo(
    () => data?.splitOptions?.[config.splitDimension] ?? [],
    [data, config.splitDimension],
  );
  const cutoffs = useMemo(
    () =>
      resolveSelectedCohortCutoffs({
        enabled: config.showOnlyFullCohorts,
        splitDimension: config.splitDimension,
        isChronological: isChronologicalSplit(
          historyMeta,
          config.splitDimension,
        ),
        splitOptions,
        excludedSplitOptions: config.excludedSplitOptions,
      }),
    [
      splitOptions,
      historyMeta,
      config.showOnlyFullCohorts,
      config.splitDimension,
      config.excludedSplitOptions,
    ],
  );
  // Trimmed before useLineChartParams so Y domains and noData reflect only the visible window.
  const chartSource = useMemo(
    () =>
      narrowToVisibleSnapshots(
        trimImmatureCohorts(
          (data?.data || []) as DataPoint<typeof CHART_BASE_DIMENSION>[],
          CHART_BASE_DIMENSION,
          cutoffs,
        ),
        config.excludedSplitOptions,
      ),
    [data, cutoffs, config.excludedSplitOptions],
  );
  const {
    noData,
    lines,
    leftY,
    rightY,
    data: chartData,
  } = useLineChartParams({
    meta: historyMeta,
    chart: config,
    data: chartSource,
    splitOptions,
    defaultSplitOption: CHART_BASE_DIMENSION,
    excludedSplitOptions: config.excludedSplitOptions,
  });

  useEffect(() => {
    onDataChange?.(data);
  }, [data, onDataChange]);

  const noSelection = lines.length === config.excludedSplitOptions.length;
  const everyCohortImmature =
    cutoffs.size > 0 &&
    !!data?.data.length &&
    !hasVisibleCohortData(chartSource, config.excludedSplitOptions);
  const hasCompleteChart =
    !!data &&
    !noData &&
    !everyCohortImmature &&
    !leftY?.noData &&
    !rightY?.noData &&
    !noSelection;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LineChartWrapper
        dataStatus={dataStatus}
        keepContentWhileLoading={hasCompleteChart}
        noData={noData || everyCohortImmature}
        noDataMessage={
          everyCohortImmature ? IMMATURE_COHORT_MESSAGE : undefined
        }
        noSelection={noSelection}
      >
        <Suspense fallback={<Loader active />}>
          <SeriesChart
            sx={{ border: 'none' }}
            syncYAxesDomains={config.syncedYAxes}
            excludedLines={config.excludedSplitOptions}
            highlightedLine={config.highlightLineOnHover ? hoveredOption : null}
            isMediaContent={isMediaContent}
            isDateContent={isDateContent}
            xAxisDataKey={CHART_BASE_DIMENSION}
            xAxisFormatter={formatSnapshotDttm}
            leftY={leftY}
            rightY={rightY}
            data={chartData}
            lines={lines}
            leftSeries={config.leftAxisSeries}
            rightSeries={config.rightAxisSeries}
            palette={TABLEAU_20_COLORS}
          />
        </Suspense>
      </LineChartWrapper>
    </Box>
  );
}
